// src/components/Lead/SmsModal.js
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '../../components/shared/Modal';
import formatPhone from '../../components/shared/formatPhone';
import TextArea from '../../components/shared/TextArea';
import { logCommunication } from '../../services/leadLogService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { FaSpinner, FaArrowRight, FaArrowLeft, FaImage, FaTimes, FaPaperPlane, FaClock } from 'react-icons/fa';
import { sendSms, getLeadCommunicationHistory } from '../../services/communicationService';
import { createScheduledMessage } from '../../services/scheduledMessageService';
import TemplateSelector from '../follow-ups/components/TemplateSelector';
import { replaceTemplateVariables } from '../follow-ups/utils/templateUtils';
import { getLeadWithRelated, getLeadEstimates } from './leadService';
import { compressImage, MMS_OPTIONS } from '../../utils/imageCompression';

const MessageCounter = styled.div`
  font-size: 14px;
  color: ${(props) => (props.charCount > 249 ? 'red' : props.theme.colors.textPrimary)};
  text-align: right;
`;

const PhoneNumber = styled.div`
  font-size: 1.2em;
  color: ${(props) => (props.theme.colors.textPrimary)};
  margin-bottom: 1em;
`;

const StatusMessage = styled.div`
  font-size: 14px;
  color: ${(props) => (props.error ? 'red' : 'green')};
  margin-bottom: 10px;
`;

const MessageHistoryContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 15px;
  padding: 5px 0;
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
  border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
  position: relative;
  word-wrap: break-word;

  ${props => props.sent ? `
    background-color: ${props.theme.colors.primary}33;
    margin-left: auto;
    border-bottom-right-radius: 4px;
  ` : `
    background-color: ${props.theme.colors.surface};
    margin-right: auto;
    border-bottom-left-radius: 4px;
  `}
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.textSecondary};
  text-align: right;
  margin-top: 4px;
`;

const AttachRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

// Matches the multer limit on the API's MMS upload route.
const MAX_MMS_BYTES = 5 * 1024 * 1024;

const AttachBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  cursor: pointer;
  &:hover { background: ${props => props.theme.colors.inputGray}; color: ${props => props.theme.colors.textPrimary}; }
  &:disabled { opacity: 0.6; cursor: default; }
  .icon-spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ImagePreview = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 10px;
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.inputGray};
  img { display: block; max-width: 180px; max-height: 120px; object-fit: cover; }
`;

const RemoveImg = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const ModeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  justify-content: center;
  padding: 8px 12px;
  border-radius: ${props => props.theme.borderRadius};
  border: 1.5px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.inputGray + '44'};
  background: ${props => props.active ? props.theme.colors.primary + '22' : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${props => props.theme.colors.primary}88; }
`;

const ScheduleRow = styled.div`
  margin-bottom: 12px;
  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: 6px;
  }
  .react-datepicker-wrapper { width: 100%; }
  input {
    width: 100%;
    padding: 8px 12px;
    background: ${props => props.theme.colors.background};
    border: 1.5px solid ${props => props.theme.colors.inputGray}44;
    border-radius: ${props => props.theme.borderRadius};
    color: ${props => props.theme.colors.textPrimary};
    font-size: 0.9rem;
  }
`;

const SmsModal = ({ isOpen, onClose, lead, userId }) => {
  const [message, setMessage] = useState('');
  const charCount = message.length;
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [preparingMedia, setPreparingMedia] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);
  // Lead enriched with the fields template variables need ({book_a_call_url},
  // {city}, {quote_url}...). Callers often pass only id/name/phone, so we fetch
  // the rest by id rather than depending on every caller to provide them.
  const [templateLead, setTemplateLead] = useState(lead);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && lead) {
      fetchMessageHistory();
      setMediaFile(null);
      setMediaPreview(null);
      setScheduleMode(false);
      setScheduledAt(null);
      setTemplateLead(lead);
      enrichLeadForTemplates();
    }
  }, [isOpen, lead]);

  // Pull book_a_call_url / city / estimates by lead id when the caller didn't
  // already supply them, so template variables resolve from any entry point.
  const enrichLeadForTemplates = async () => {
    if (!lead?.id) return;
    if (lead.book_a_call_url && lead.estimates) return; // already complete
    try {
      const [detailRes, estimatesRes] = await Promise.all([
        lead.book_a_call_url ? Promise.resolve(null) : getLeadWithRelated(lead.id).catch(() => null),
        lead.estimates ? Promise.resolve(null) : getLeadEstimates(lead.id).catch(() => null),
      ]);
      setTemplateLead(prev => ({
        ...(detailRes?.data || {}),
        ...prev,
        book_a_call_url: prev?.book_a_call_url || detailRes?.data?.book_a_call_url,
        city: prev?.city || detailRes?.data?.city,
        estimates: prev?.estimates || estimatesRes?.data || [],
      }));
    } catch (err) {
      console.error('Failed to enrich lead for templates:', err);
    }
  };

  const fetchMessageHistory = async () => {
    if (!lead?.id) return;
    try {
      setIsLoading(true);
      const response = await getLeadCommunicationHistory(lead.id);
      const smsMessages = response.filter(item => item.type === 'sms');
      smsMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setMessageHistory(smsMessages);
    } catch (error) {
      console.error('Error fetching message history:', error);
      setStatusMessage('Failed to load message history');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    // Shrink the photo first, then check the size — a phone camera shot is
    // always over the limit as-is, but is well under it once resized.
    setPreparingMedia(true);
    try {
      const prepared = await compressImage(file, MMS_OPTIONS);
      if (prepared.size > MAX_MMS_BYTES) {
        setStatusMessage('Image is too large to send, even after compression');
        setIsError(true);
        return;
      }
      setMediaFile(prepared);
      setMediaPreview(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(prepared);
      });
    } finally {
      setPreparingMedia(false);
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendSms = async () => {
    setIsLoading(true);
    setStatusMessage('');
    setIsError(false);

    try {
      if (!lead || !lead.phone) {
        setStatusMessage('Error: Lead information is incomplete');
        setIsError(true);
        setIsLoading(false);
        return;
      }

      if (!message.trim()) {
        setStatusMessage('Error: Message cannot be empty');
        setIsError(true);
        setIsLoading(false);
        return;
      }

      // ── Schedule for later branch ──
      if (scheduleMode) {
        if (!scheduledAt || scheduledAt.getTime() <= Date.now()) {
          setStatusMessage('Error: Pick a date/time in the future');
          setIsError(true);
          setIsLoading(false);
          return;
        }
        if (mediaFile) {
          setStatusMessage('Image attachments are not supported for scheduled messages');
          setIsError(true);
          setIsLoading(false);
          return;
        }

        await createScheduledMessage({
          scheduled_time: scheduledAt.toISOString(),
          template_id: selectedTemplateId || null,
          recipients: [{
            lead_id: lead.id,
            to_number: lead.phone,
            body: message.trim(),
            recipient_name: lead.name,
          }],
        });

        setStatusMessage(`Message scheduled for ${scheduledAt.toLocaleString()}.`);
        setMessage('');
        setScheduledAt(null);
        setScheduleMode(false);
        setIsLoading(false);

        try {
          await logCommunication(lead.id, 'SMS', `Scheduled SMS for ${scheduledAt.toLocaleString()}: "${message}"`, userId);
        } catch (logError) {
          console.error('Error logging communication (message was scheduled):', logError);
        }
        return;
      }

      const response = await sendSms(
        lead.phone,
        message.trim(),
        lead.id,
        mediaFile
      );

      setMessageHistory(prev => [...prev, {
        id: response.sms?.sms_id,
        direction: 'outbound',
        body: message,
        timestamp: new Date().toISOString()
      }]);

      setStatusMessage(mediaFile ? 'MMS sent successfully.' : 'SMS sent successfully.');
      setMessage('');
      removeMedia();
      setIsLoading(false);

      try {
        await logCommunication(lead.id, 'SMS', `Message: "${message}" sent to ${lead.phone}`, userId);
      } catch (logError) {
        console.error('Error logging communication (SMS was sent):', logError);
      }

    } catch (error) {
      console.error('Error sending SMS:', error);
      const apiMsg = error.response?.data?.message;
      setStatusMessage(apiMsg || (scheduleMode ? 'Failed to schedule message.' : 'Failed to send SMS.'));
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <Modal
      title={`Send ${mediaFile ? 'MMS' : 'SMS'} to ${lead.name}`}
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={handleSendSms}
      confirmDisabled={isLoading || message.length === 0}
      confirmText={mediaFile ? "Send MMS" : "Send"}
    >
      {statusMessage && <StatusMessage error={isError}>{statusMessage}</StatusMessage>}

      <PhoneNumber>{formatPhone(lead?.phone || '')}</PhoneNumber>

      {messageHistory.length > 0 && (
        <MessageHistoryContainer>
          {messageHistory.map(msg => (
            <MessageBubble
              key={msg.id}
              sent={msg.direction === 'outbound'}
            >
              {msg.direction === 'outbound' ? (
                <FaArrowRight size={10} style={{ marginRight: '5px', color: '#888' }} />
              ) : (
                <FaArrowLeft size={10} style={{ marginRight: '5px', color: '#888' }} />
              )}
              {msg.body}
              <MessageTime>{formatMessageTime(msg.timestamp)}</MessageTime>
            </MessageBubble>
          ))}
        </MessageHistoryContainer>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <FaSpinner className="icon-spin" style={{ fontSize: '24px' }} />
          <p>Sending message...</p>
        </div>
      ) : (
        <>
          <TemplateSelector
            selectedId={selectedTemplateId}
            onSelect={(template) => {
              if (template) {
                setSelectedTemplateId(template.id);
                const src = templateLead || lead;
                const latestEstimate = src.estimates?.find(e => e.publicToken) || src.estimates?.[0] || null;
                setMessage(replaceTemplateVariables(template.body, src, latestEstimate));
              } else {
                setSelectedTemplateId(null);
                setMessage('');
              }
            }}
          />
          <ModeToggle>
            <ModeButton type="button" active={!scheduleMode} onClick={() => setScheduleMode(false)}>
              <FaPaperPlane size={12} /> Send now
            </ModeButton>
            <ModeButton type="button" active={scheduleMode} onClick={() => setScheduleMode(true)}>
              <FaClock size={12} /> Schedule for later
            </ModeButton>
          </ModeToggle>
          {scheduleMode && (
            <ScheduleRow>
              <label>Deliver on</label>
              <DatePicker
                selected={scheduledAt}
                onChange={(date) => setScheduledAt(date)}
                showTimeSelect
                timeIntervals={15}
                minDate={new Date()}
                dateFormat="MM/dd/yyyy h:mm aa"
                placeholderText="Select date & time..."
              />
            </ScheduleRow>
          )}
          <TextArea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={250}
            placeholder="Enter your message..."
            style={{ width: '100%', height: '100px', marginBottom: '10px' }}
            disabled={isLoading}
          />
          <AttachRow>
            <AttachBtn
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={preparingMedia}
            >
              {preparingMedia
                ? <><FaSpinner className="icon-spin" /> Preparing...</>
                : <><FaImage /> Attach Image</>}
            </AttachBtn>
            <MessageCounter charCount={charCount}>{charCount}/250</MessageCounter>
          </AttachRow>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {mediaPreview && (
            <ImagePreview>
              <img src={mediaPreview} alt="Attachment" />
              <RemoveImg onClick={removeMedia}><FaTimes /></RemoveImg>
            </ImagePreview>
          )}
        </>
      )}
    </Modal>
  );
};

export default SmsModal;
